import React, { useState, useEffect, useCallback } from 'react';
import { usersApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './Users.css';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'market_staff', label: 'Market Staff' },
];

const COMPANIES = [
  { value: 'AR', label: 'Apple Ridge Farm' },
  { value: 'CC', label: 'Collective Culture' },
  { value: 'both', label: 'Both' },
];

const EMPTY_FORM = {
  name: '', email: '', role: 'market_staff',
  company: 'AR', is_active: true,
};

function roleBadge(role) {
  const classes = { admin: 'badge-admin', manager: 'badge-manager', market_staff: 'badge-staff' };
  const labels = { admin: 'Admin', manager: 'Manager', market_staff: 'Staff' };
  return <span className={`role-badge ${classes[role] || ''}`}>{labels[role] || role}</span>;
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('active');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await usersApi.getAll();
      setUsers(data.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openNew() { setForm(EMPTY_FORM); setEditing({}); }
  function openEdit(user) { setForm({ ...user }); setEditing(user); }
  function closeForm() { setEditing(null); setForm(EMPTY_FORM); }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing?.id) { await usersApi.update(form); }
      else { await usersApi.create(form); }
      await load();
      closeForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(user) {
    try {
      await usersApi.toggleActive(user.id, !user.is_active);
      setUsers((us) => us.map((u) => u.id === user.id ? { ...u, is_active: !u.is_active } : u));
    } catch (err) {
      setError(err.message);
    }
  }

  const filtered = users.filter((u) => {
    if (filter === 'active') return u.is_active;
    if (filter === 'inactive') return !u.is_active;
    return true;
  });

  return (
    <div className="page">
      <AdminPageHeader
        title="Users"
        subtitle={`${users.filter((u) => u.is_active).length} active users`}
        action={<button className="btn-primary" onClick={openNew}>+ Add User</button>}
      />
      <div className="page-body">
        {error && <div className="error-banner">{error} <button onClick={load}>Retry</button></div>}
        <div className="filter-bar">
          {['active', 'inactive', 'all'].map((f) => (
            <button key={f} className={`filter-btn ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="loading-state">Loading users...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            {users.length === 0 ? 'No users yet. Add your first team member.' : 'No users match this filter.'}
          </div>
        ) : (
          <div className="markets-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Role</th>
                  <th>Company</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className={u.is_active ? '' : 'row-inactive'}>
                    <td className="td-name">{u.name}</td>
                    <td className="td-email">{u.email}</td>
                    <td>{roleBadge(u.role)}</td>
                    <td>{COMPANIES.find((c) => c.value === u.company)?.label || u.company}</td>
                    <td>
                      <button className={`status-toggle ${u.is_active ? 'active' : 'inactive'}`} onClick={() => toggleActive(u)}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td><button className="btn-edit" onClick={() => openEdit(u)}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing !== null && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing?.id ? 'Edit User' : 'Add User'}</h2>
              <button className="modal-close" onClick={closeForm}>x</button>
            </div>
            <form onSubmit={handleSave} className="modal-form">
              <div className="field">
                <label>Full Name *</label>
                <input name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Jane Smith" autoFocus />
              </div>
              <div className="field">
                <label>Google Email *</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="e.g. jane@gmail.com" />
              </div>
              <div className="field-row">
                <div className="field">
                  <label>Role *</label>
                  <select name="role" value={form.role} onChange={handleChange}>
                    {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div className="field">
                  <label>Company *</label>
                  <select name="company" value={form.company} onChange={handleChange}>
                    {COMPANIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="field">
                <label className="checkbox-label">
                  <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
                  Active
                </label>
              </div>
              {error && <div className="form-error">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editing?.id ? 'Save Changes' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
